import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Clock } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { SkeletonRows } from "@/components/app/Common";
import { api, get, errMsg } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { STAFF } from "@/lib/nav";
import { blankTemplate, estimateMinutes } from "@/lib/library";
import { BasicsStep } from "@/pages/assessments/builder/BasicsStep";
import { SectionsStep } from "@/pages/assessments/builder/SectionsStep";
import { ScoringStep } from "@/pages/assessments/builder/ScoringStep";
import { PreviewStep } from "@/pages/assessments/builder/PreviewStep";
import { useMiniTour } from "@/components/app/miniTours";
import { UseForJobDialog } from "@/pages/assessments/library/UseForJobDialog";

const STEPS = [["basics", "Basics"], ["sections", "Sections"], ["scoring", "Scoring"], ["preview", "Preview"]];
const FIELDS = ["name", "description", "time_limit_min", "expiry_days", "sections", "weights"];

export default function BuilderPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const { user } = useAuth();
  const canEdit = STAFF.includes(user.role);
  const step = params.get("step") || (canEdit ? "basics" : "preview");
  const forJob = params.get("job");
  const [using, setUsing] = useState(null);
  const { data, isLoading } = useQuery({ queryKey: ["assessment-tpl", id], queryFn: () => get(`/assessment-library/${id}`), enabled: !!id });
  const [t, setT] = useState(id ? null : blankTemplate());
  const [busy, setBusy] = useState(false);
  const tRef = useRef(t);
  tRef.current = t;
  useMiniTour("builder", !!t && step === "sections");
  useEffect(() => { if (data) setT(Object.fromEntries(FIELDS.map((f) => [f, data[f]]))); }, [data]);
  if (id && (isLoading || !t)) return <SkeletonRows rows={5} />;
  const set = (patch) => setT((x) => ({ ...x, ...patch }));
  const go = (s) => setParams({ step: s, ...(forJob ? { job: forJob } : {}) });

  const save = async () => {
    setBusy(true);
    try {
      const r = id ? await api.put(`/assessment-library/${id}`, t) : await api.post("/assessment-library", t);
      const saved = r.data;
      if (r.data.versioned) toast.info(`Saved as version ${saved.version}. People already invited keep the version they were sent.`);
      qc.invalidateQueries({ queryKey: ["assessment-library"] });
      qc.invalidateQueries({ queryKey: ["assessment-tpl"] });
      if (saved.warnings?.length) toast.warning(`Saved. Fix ${saved.warnings.length} item(s) before using it for a job.`);
      else if (forJob) {
        await api.post(`/jobs/${forJob}/assessment-template`, { template_id: saved.id });
        qc.invalidateQueries({ queryKey: ["job-assessment", forJob] });
        toast.success("Saved and attached to the job");
        return nav(`/app/jobs/${forJob}/assessments`);
      } else toast.success("Saved. Ready to use for a job.");
      if (saved.id !== id) nav(`/app/assessments/${saved.id}?step=${step}${forJob ? `&job=${forJob}` : ""}`, { replace: true });
    } catch (e) {
      const w = e?.response?.data?.detail?.warnings;
      toast.error(w ? `${errMsg(e)}: ${w.join("; ")}` : errMsg(e));
    } finally { setBusy(false); }
  };

  const autosave = async () => {
    if (!id || data?.status === "archived" || data?.active_invites) return false;
    try { await new Promise((r) => setTimeout(r, 0)); await api.put(`/assessment-library/${id}`, tRef.current); return true; } catch { return false; }
  };
  const idx = STEPS.findIndex(([k]) => k === step);
  const props = { t, set, canEdit, templateId: id, data, autosave };
  return (
    <div className="space-y-6" data-testid="assessment-builder" data-tour="assessment-builder">
      <div className="flex flex-wrap items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => nav(forJob ? `/app/jobs/${forJob}/assessments` : "/app/assessments")} data-testid="builder-back"><ArrowLeft size={16} /> {forJob ? "Back to job" : "Assessments"}</Button>
        <h1 className="font-display text-2xl font-semibold">{t.name || "New assessment"}</h1>
        {data && <span className={`text-sm ${data.warnings?.length ? "text-caution" : "text-success"}`} data-testid="builder-status">{data.warnings?.length ? "Needs fixes" : "Ready to use"} · v{data.version}</span>}
        <span className="ml-auto inline-flex items-center gap-1 rounded-full bg-surface-2 px-3 py-1 text-sm" data-testid="builder-estimate"><Clock size={14} /> about {estimateMinutes(t.sections)} min</span>
      </div>
      <ol className="flex flex-wrap gap-2" aria-label="Builder steps" data-testid="builder-steps">
        {STEPS.map(([k, label], i) => (
          <li key={k}><button onClick={() => go(k)} aria-current={k === step ? "step" : undefined} data-testid={`builder-step-${k}`}
            className={`rounded-full border px-4 py-1.5 text-sm transition-colors ${k === step ? "border-primary bg-primary text-white" : "border-line bg-white text-ink-secondary hover:bg-surface-2"}`}>
            {i + 1}. {label}</button></li>
        ))}
      </ol>
      {forJob && <p className="text-sm text-ink-secondary" data-testid="builder-for-job">When you save, this assessment is attached to the job and you go back to it.</p>}
      {data?.warnings?.length > 0 && (
        <div className="rounded-[6px] border border-[#E8D7B0] bg-caution-tint px-4 py-3 text-sm text-caution" role="status" data-testid="builder-warnings">
          <p className="font-medium">Fix these before using it for a job:</p>
          <ul className="ml-5 list-disc">{data.warnings.map((w) => <li key={w}>{w}</li>)}</ul>
        </div>
      )}
      {step === "basics" && <BasicsStep {...props} />}
      {step === "sections" && <SectionsStep {...props} />}
      {step === "scoring" && <ScoringStep {...props} />}
      {step === "preview" && <PreviewStep {...props} />}
      <div className="flex flex-wrap items-center gap-2 border-t border-line pt-4">
        {idx > 0 && <Button variant="outline" onClick={() => go(STEPS[idx - 1][0])} data-testid="builder-prev">Back</Button>}
        {idx < 3 && <Button variant="outline" onClick={() => go(STEPS[idx + 1][0])} data-testid="builder-next">Next: {STEPS[idx + 1][1]}</Button>}
        {canEdit && <span className="ml-auto flex gap-2">
          {id && !forJob && !data?.warnings?.length && <Button variant="outline" disabled={busy} onClick={() => setUsing(data)} data-testid="builder-use-for-job">Use for a job</Button>}
          <Button disabled={busy || t.name.trim().length < 2} onClick={save} data-testid="builder-save">{forJob ? "Save and attach to job" : "Save"}</Button>
        </span>}
        {!canEdit && <p className="ml-auto text-sm text-ink-muted" data-testid="builder-readonly">View only. Ask a recruiter to make changes.</p>}
      </div>
      <UseForJobDialog template={using} onClose={() => setUsing(null)} />
    </div>
  );
}
